import { message, Modal } from 'antd'
import Cookies from 'js-cookie'
import { push } from 'react-router-redux'
import { take, put, call, select } from 'redux-saga/effects'
import { handlerCnames } from '@openplatform/utils/index'
import {
  // LOGIN_REQUEST,
  INITIAL_USER_INFO,
  INITIAL_REGISTER_INFO,
  // LOGIN_START,
  LOGIN_SUCCESS,
  // LOGIN_ACCOUNT_OR_PASSWORD_ERROR,
  // LOGIN_TIMEOUT,
  GET_DEVELOPER_INFO_SUCCESS,
  GET_USER_INFO_SUCCESS,
  LOGINOUT,
  LOGINOUT_SUCCESS,
  REFRESH_BROWSER,
  REGISTER_END,
  DOMAINS,
  CHANGE_EXAMINETYPE,
  CHANGE_LOGIN
} from '@openplatform/store/actionTypes/homepage'
import { resetHost, handleConsoleRedirect } from '@openplatform/utils'
import * as adminServices from '@openplatform/request/api/admin'
import * as services from '@openplatform/request/api/login'
import { immutableSelector, setUserInfo } from '@/utils/index'
import { getCookiesDomain } from '@/config/environment'

function getLang() {
  const { getLocaleLanguage } = window as any
  const { lang } = getLocaleLanguage(window.navigator, '')
  const storageLang = localStorage.getItem('curLang')
  return storageLang || lang
}

// Logout
function* loginOut(): Generator<any, any, any> {
  const token = yield select((state) => immutableSelector(state).user.userInfo.apptoken)
  try {
    const lang = localStorage.getItem('curLang')
    yield call(services.loginOut, { login_token: token })
    localStorage.clear()
    // Clear WebIDE cookie information when logging out
    const d = new Date()
    d.setTime(d.getTime() - 7 * 24 * 60 * 60 * 1000)
    const expires = `expires=${d.toUTCString()}`
    document.cookie = `dev_apptoken=''; domain=.huami.com; path=/;expires=${expires}`
    localStorage.setItem('curLang', lang || 'zh')
    Cookies.set('apptoken', '', {
      expires: -10000000,
      domain: getCookiesDomain()
    })
    Cookies.set('userid', '', {
      expires: -10000000,
      domain: getCookiesDomain()
    })
    Cookies.set('hm-user-login-info', '', {
      expires: -10000000,
      domain: getCookiesDomain()
    })
    yield call(resetHost)
    yield put({ type: LOGINOUT_SUCCESS })
    yield put({ type: CHANGE_EXAMINETYPE, payload: 0 })
  } catch (error) {
    console.log(error)
  }
}

// Query developer authentication information
function* getDeveloperInfo(): Generator<any, any, any> {
  try {
    const authInfo = yield call(adminServices.getAuthInfo)
    if (authInfo?.error?.name === '401') {
      yield call(loginOut)
      Modal.warning({
        title: getLang() === 'zh' ? '登录凭证过期' : 'invalid token',
        okText: getLang() === 'zh' ? '去登录' : 'Login',
        onOk: () => {
          handleConsoleRedirect()
        }
      })
      return
    }
    yield put({ type: GET_DEVELOPER_INFO_SUCCESS, payload: authInfo || {} })
    return authInfo
  } catch (error) {
    console.log(error)
  }
}

// Initialize user information after login
function* initialUserInfo(): Generator<any, any, any> {
  try {
    let userInfo: any = Cookies.get('hm-user-login-info')
    let app_token: string
    let user_id: string  
    let login_token = ''
    let contact = ''
    let nickname = ''
    let third_id = ''
    let country_code = ''
    let domains: any[] = []

    if (!userInfo) {
      // Fallback logic: get third-party login information from cookies
      const cookieApptoken = Cookies.get('apptoken')
      const cookieUserid = Cookies.get('userid') 
      const cookieName = Cookies.get('name')
      
      if (cookieApptoken && cookieUserid && cookieName) {
        // Construct simplified user information object
        app_token = cookieApptoken
        user_id = cookieUserid
        nickname = cookieName
        contact = '' // Third-party login may not have this information
        third_id = ''
        country_code = ''
      } else {
        return
      }
    } else {
      userInfo = JSON.parse(userInfo)
      domains = userInfo.domains || []
      const tokenInfo = userInfo.token_info || {}
      const registInfo = userInfo.regist_info || {}
      const thirdpartyInfo = userInfo.thirdparty_info || {}
      
      app_token = tokenInfo.app_token
      user_id = tokenInfo.user_id
      login_token = tokenInfo.login_token || ''
      country_code = registInfo.country_code || ''
      contact = thirdpartyInfo.email || ''
      nickname = thirdpartyInfo.nickname || ''
      third_id = thirdpartyInfo.third_id || ''
    }

    yield call(handlerCnames, domains)
    if (user_id) {
      const { data, status } = yield call(services.getUserInfoFromInterface, user_id, app_token)
      if (status === 401 && data.code === '0102') {
        message.error('login timeout')
        return
      }
      // Get user registration information
      const { userData } = yield call(services.userRegistration, user_id, app_token)
      if (data) {
        // Update user information
        if (userInfo) {
          userInfo.thirdparty_info.nickname = data.nickName
          if (userData?.country_code) {
            userInfo.regist_info.country_code = userData.country_code
          }
        }
        
        yield put({ type: GET_USER_INFO_SUCCESS, payload: data })
        yield put({ type: CHANGE_LOGIN, data: true })
        yield call(setUserInfo, {
          userid: user_id,
          apptoken: app_token,
          nickName: data?.nickName || nickname,
          login_token,
          contact,
          country: (userData?.country_code?.toLocaleLowerCase() as string) ?? (country_code?.toLocaleLowerCase() as string),
          iconUrl: data?.iconUrl,
          userIntroduction: data?.userIntroduction
        })
        localStorage.setItem('third_id', third_id)
      }
      yield call(getDeveloperInfo)
      yield put(push(''))
    }
    
    // Construct login success payload, handle third-party login scenario
    const loginPayload = userInfo || {
      token_info: { app_token, user_id, login_token },
      regist_info: { country_code },
      thirdparty_info: { nickname, email: contact, third_id },
      domains
    }
    
    yield put({ type: LOGIN_SUCCESS, payload: loginPayload })
    yield put({ type: DOMAINS, payload: domains })
  } catch (error) {
    console.log(error)
  }
}

// Initialize user information after registration
function* initialRegisterInfo(): Generator<any, any, any> {
  try {
    let regLastRes: any = Cookies.get('hm-user-login-info')
    if (!regLastRes) return
    regLastRes = JSON.parse(regLastRes)

    const {
      token_info: { app_token, user_id, login_token },
      regist_info: { country_code },
      thirdparty_info: { nickname, icon, third_id, email },
      domains,
      domain
    } = regLastRes
    // Domain
    yield call(handlerCnames, domains)
    yield put({ type: DOMAINS, payload: domain })
    // User information
    yield put({
      type: REGISTER_END,
      payload: {
        userid: user_id,
        apptoken: app_token,
        nickName: nickname,
        country: country_code.toLocaleLowerCase(),
        avatar: icon,
        developerID: third_id,
        email
      }
    })
    // Store to localStorage
    yield call(setUserInfo, {
      userid: user_id,
      apptoken: app_token,
      nickName: nickname,
      login_token,
      contact: email,
      iconUrl: '',
      country: country_code.toLocaleLowerCase(),
      userIntroduction: ''
    })
    yield put({
      type: CHANGE_EXAMINETYPE,
      data: 0
    })
    yield put({ type: CHANGE_LOGIN, data: true })
  } catch (error) {
    console.log(error)
  }
}

// Query user detailed information
function* getUserDetail({ userid, apptoken }: { userid: string; apptoken: string }): Generator<any, any, any> {
  try {
    const userInfo = yield call(services.getUserInfoFromInterface, userid, apptoken)
    console.log(userInfo)
    yield put({ type: GET_USER_INFO_SUCCESS, payload: userInfo?.data })
    return userInfo
  } catch (error) {
    console.log(error)
  }
}

// Unified login follow-up operations
export function* initialUserInfoFlow(): Generator<any, any, any> {
  while (true) {
    yield take(INITIAL_USER_INFO)
    yield call(initialUserInfo)
  }
}

// Unified registration follow-up operations
export function* initialRegisterInfoFlow(): Generator<any, any, any> {
  while (true) {
    yield take(INITIAL_REGISTER_INFO)
    yield call(initialRegisterInfo)
  }
}

// Logout
export function* loginOutFlow(): Generator<any, any, any> {
  while (true) {
    yield take(LOGINOUT)
    yield call(loginOut)
    yield put(push(''))
  }
}

// Refresh page flow
export function* refreshFlow(): Generator<any, any, any> {
  while (true) {
    yield take(REFRESH_BROWSER)
    const apptoken: string | null = localStorage.getItem('apptoken')
    const userid: string | null = localStorage.getItem('userid')
    if (!apptoken || !userid) return
    yield call(resetHost)
    yield call(getDeveloperInfo)
    yield call(getUserDetail, {
      userid,
      apptoken
    })
  }
}
